/**
 * Rutor: ett generiskt, färdigformaterat format för informationsrutor i
 * AI-svaret (i dag lathundar, ICD-10/KVÅ-koder och kalkylatorlänkar från
 * verktyg.internetmedicin.se).
 *
 * KONTRAKT (gäller varje yta som visar assistentens svar):
 *
 * 1. Klienten uppger i chattanropet att den kan rita rutor:
 *      { ..., capabilities: ['rutor-v1'] }        (RUTOR_CAPABILITY)
 *    Utan den skickar servern varken rutor eller markörer - en äldre klient
 *    (t.ex. sajtens AI-ruta i dag) får exakt samma svar som förut.
 * 2. Servern skickar dataposten `rutor` i strömmen (AI SDK data stream):
 *      2:[{"type":"rutor","content":<RutorPart>}]
 *    en tidig före svarstexten och en slutlig efter (bara när svaret visar
 *    något). Klienten använder den SISTA (som för `sources`).
 * 3. I svarstexten står markörer ensamma på en rad, `[[ruta:v1]]`, eller mitt
 *    i en mening (ritas då som en länk). Klienten ersätter markören med rutan
 *    med samma id; okänt id ritas som ingenting, aldrig som råtext.
 * 4. `footer` är rader under svaret (t.ex. länkrad till verktyg).
 * 5. Framåtkompatibilitet: en klient IGNORERAR okända värden på block `type`,
 *    `icon` och `layout` (ritar ingenting / en neutral ikon) och okända fält
 *    (t.ex. `copyable`) - nya rutsorter får aldrig kunna krascha en yta som
 *    inte hunnit uppdateras.
 * 6. Avbryts svaret finns bara den tidiga posten; markörer löses då mot den.
 * 7. Text som visas utan rutor (teaser, förhandsvisning, kopiering) tar bort
 *    markörerna (replaceRutaMarkers / rutaMarkersForCopy); under strömning
 *    döljs en påbörjad markör (hidePartialRutaMarker). `footer` följer inte
 *    med vid kopiering.
 *
 * Servern bygger rutorna färdiga (app/utils/rutor/*.server.ts). En klient
 * behöver bara kunna rita blocktyperna nedan - en ny rutsort eller ändrat
 * innehåll kräver ingen klientändring på någon yta. Den här filen är
 * KONTRAKTETS SPECIFIKATION (typerna, markörformatet, splitRutaSegments och
 * hjälpfunktionerna i punkt 7 som referens för tolkningen): andra ytor
 * implementerar det själva mot chattströmmen (punkt 2), de kopierar inte
 * filen (se "Delad logik med sajten" i CLAUDE.md). Därför är kontraktet
 * versionerat och additivt. Renderaren i
 * app/components/rutor/ruta-view.tsx är assistentens egen presentation; sajten
 * ritar rutorna i sin egen stil.
 *
 * Klientsäker (typer och ren sträng-/regex-logik).
 */

/** Klientens förmåga: kan rita rutor ur `rutor`-posten och tolka markörerna. */
export const RUTOR_CAPABILITY = 'rutor-v1'

/** Inlinesegment: text, ev. länk (http/https), fet stil, kodformat. */
export type Span = {
	text: string
	href?: string
	bold?: boolean
	/** Kodformat (t.ex. ICD-koder). */
	mono?: boolean
	/**
	 * Visa en kopieringsknapp som kopierar `text` (t.ex. en diagnoskod eller
	 * dess benämning till journalanteckningen). Valfritt - en yta utan stöd
	 * ritar bara texten.
	 */
	copyable?: boolean
}
export type Inline = Span[]

export type RutaCell = {
	content: Inline
	header?: boolean
	colSpan?: number
	rowSpan?: number
}

/** Blocktyper. Fler kan tillkomma (additivt) - okänd typ ritas som ingenting. */
export type RutaBlock =
	| { type: 'text'; content: Inline; muted?: boolean }
	| { type: 'heading'; text: string }
	| {
			type: 'list'
			ordered?: boolean
			items: Inline[]
	  }
	| { type: 'table'; rows: { cells: RutaCell[]; highlight?: boolean }[] }
	| { type: 'image'; src: string; alt: string; href?: string }
	| { type: 'links'; links: { label: string; href: string }[] }
	/** Hopfällt avsnitt (t.ex. referenser). */
	| { type: 'details'; summary: string; blocks: RutaBlock[] }

export type RutaIcon = 'book' | 'hash' | 'calculator' | 'wrench'

export type Ruta = {
	/** Id som markören använder: "v2" (ev. med del: "x1:del"). */
	id: string
	title: string
	/** Vad rutan är ("Lathund", "ICD-10-SE"); titeln säger vilket. */
	subtitle?: string
	icon: RutaIcon
	/**
	 * Källmärkning, bara för innehåll från någon annan än
	 * Internetmedicin (t.ex. "Från Fass", som kräver märkningen). Utelämnas för
	 * vårt eget innehåll - det är underförstått.
	 */
	source?: string
	/**
	 * 'box' = vanlig ruta. 'link' = hela rutan är en länk (kalkylatorer):
	 * titel + beskrivning + knapp `linkLabel`.
	 */
	layout: 'box' | 'link'
	href?: string
	linkLabel?: string
	description?: string
	blocks: RutaBlock[]
	/** Långt innehåll: visa en avklippt ruta med "Visa hela". */
	collapse?: boolean
	/** Text när svaret kopieras/exporteras. */
	copy: string
}

/** Rad under svaret. */
export type RutaFooter = {
	/** Länkrad ("Verktyg: CHA₂DS₂-VA ↗"). */
	type: 'links'
	id: string
	title: string
	icon: RutaIcon
	items: { label: string; href: string; icon: RutaIcon }[]
}

/** Innehållet i dataposten `rutor`. Klienten läser den sista posten. */
export type RutorPart = {
	version: 1
	rutor: Ruta[]
	footer: RutaFooter[]
}

// ---------------------------------------------------------------------------
// Markörer

/** `[[ruta:v2]]` (id: 1-3 bokstäver + siffror, ev. `:del`) */
export const RUTA_MARKER_RE = /\[\[ruta:([a-z]{1,3}\d{1,2}(?::[a-zåäö]+)?)\]\]/g

/**
 * Döljer en påbörjad markör i slutet av en text som strömmar ("…:\n[[ruta:v"),
 * så att råtexten aldrig blinkar förbi.
 */
export function hidePartialRutaMarker(text: string): string {
	const i = text.lastIndexOf('[[')
	if (i === -1) return text
	const tail = text.slice(i)
	if (tail.includes(']]')) return text
	return /^\[\[(r(u(t(a(:[a-z0-9:åäö]*\]?)?)?)?)?)?$/.test(tail)
		? text.slice(0, i)
		: text
}

/** En markör ensam på sin rad blir ett eget block även utan tom rad. */
export function normalizeRutaMarkers(text: string): string {
	return text.replace(
		/(^|\n)[ \t]*(\[\[ruta:[a-z]{1,3}\d{1,2}(?::[a-zåäö]+)?\]\])[ \t]*(?=\n|$)/g,
		'$1\n$2\n',
	)
}

/** Allt klienten gör med svarstexten före rendering. */
export function prepareRutaText(text: string): string {
	return normalizeRutaMarkers(hidePartialRutaMarker(text))
}

/** Markörer ut ur text som ska till modellen igen (historik) eller kopieras. */
export function replaceRutaMarkers(
	text: string,
	label: (id: string) => string = () => '',
): string {
	return text.replace(RUTA_MARKER_RE, (_, id: string) => label(id))
}

export function usedRutaIds(text: string): Set<string> {
	return new Set([...text.matchAll(RUTA_MARKER_RE)].map(m => m[1]!))
}

export type RutaSegment =
	| { kind: 'text'; text: string }
	/** `inline` = markören stod mitt i en mening (inte ensam på sin rad). */
	| { kind: 'ruta'; id: string; inline: boolean }

/**
 * Står markören på en rad med bara markörer (ev. efter list- eller citat-
 * prefix)? Då är den ett block - samma regel som remark-rutor.ts stycken.
 */
function onlyMarkersOnLine(text: string, at: number): boolean {
	const lineStart = text.lastIndexOf('\n', at - 1) + 1
	const lineEnd = text.indexOf('\n', at)
	const line = text.slice(lineStart, lineEnd === -1 ? undefined : lineEnd)
	return (
		line
			.replace(/^[ \t]*(?:[-*+]|\d+\.|>)[ \t]+/, '')
			.replace(RUTA_MARKER_RE, '')
			.trim() === ''
	)
}

/**
 * Delar en svarstext i text och rutor - den enda tolkningen av markörerna,
 * för klienter som inte använder remark (t.ex. sajtens egen svarsparser).
 * remark-rutor.ts gör samma sak i markdown-trädet.
 */
export function splitRutaSegments(text: string): RutaSegment[] {
	const out: RutaSegment[] = []
	let last = 0
	for (const m of text.matchAll(RUTA_MARKER_RE)) {
		const start = m.index!
		const end = start + m[0].length
		const inline = !onlyMarkersOnLine(text, start)
		if (start > last) out.push({ kind: 'text', text: text.slice(last, start) })
		out.push({ kind: 'ruta', id: m[1]!, inline })
		last = end
	}
	if (last < text.length) out.push({ kind: 'text', text: text.slice(last) })
	return out
}

/** Bara http(s)-länkar ritas; allt annat (javascript: m.m.) blir ingen länk. */
export const safeHref = (href: string | undefined): string | undefined =>
	href && /^https?:\/\//i.test(href) ? href : undefined

/** Kopiera/exportera: markören blir rutans kopieringstext. */
export function rutaMarkersForCopy(text: string, rutor: Ruta[]): string {
	return replaceRutaMarkers(
		text,
		id => rutor.find(r => r.id === id)?.copy ?? '',
	)
}
